import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import { QueryClient } from "@tanstack/react-query";

/**
 * Hook that manages stable references for frequently changing values.
 *
 * These values (toast, t, navigate, queryClient) change on every render,
 * which would cause unnecessary useEffect re-runs if added to dependencies.
 *
 * By using refs, we always access the latest version while maintaining
 * stable references that won't trigger effect re-runs.
 *
 * This must be refactored, it's a temporary solution to avoid unnecessary useEffect re-runs without new architecture.
 */
export function useAccountRefs(
  queryClient?: QueryClient,
  impersonateCognitoId?: string | null,
) {
  const { toast } = useToast();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const toastRef = useRef(toast);
  const tRef = useRef(t);
  const navigateRef = useRef(navigate);
  const queryClientRef = useRef(queryClient);
  const impersonateCognitoIdRef = useRef<string | null>(
    impersonateCognitoId || null,
  );

  useEffect(() => {
    toastRef.current = toast;
    tRef.current = t;
    navigateRef.current = navigate;
    queryClientRef.current = queryClient;
    impersonateCognitoIdRef.current = impersonateCognitoId || null;
  }, [toast, t, navigate, queryClient, impersonateCognitoId]);

  return {
    toastRef,
    tRef,
    navigateRef,
    queryClientRef,
    impersonateCognitoIdRef,
  };
}
